import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PRICE_CATEGORIES, PRICE_PERIODS, PRICE_UNITS } from '@greenostrov/shared';
import { api } from '../lib/api';
import type { PriceRow } from '../lib/types';
import styles from './PricesPage.module.css';

export function PricesPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['prices', 'admin'],
    queryFn: () => api.listPricesAdmin(),
  });

  const create = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.createPrice(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prices', 'admin'] }),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.updatePrice(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prices', 'admin'] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.deletePrice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prices', 'admin'] }),
  });

  const [showForm, setShowForm] = useState(false);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 2 }}>Цены</h1>
          <p style={{ color: 'var(--text-muted)' }}>Активные позиции показываются на сайте</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Отмена' : 'Новая позиция'}
        </button>
      </div>

      {showForm && <NewPriceForm onSubmit={(v) => { create.mutate(v); setShowForm(false); }} />}

      {query.isLoading && <div style={{ color: 'var(--text-muted)' }}>Загрузка…</div>}
      {query.error && <div className="error">Ошибка: {(query.error as Error).message}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {query.data?.items.map((raw) => {
          const p = raw as PriceRow;
          return (
            <PriceRowItem
              key={p.id}
              price={p}
              onSave={(patch) => update.mutate({ id: p.id, patch })}
              onDelete={() => {
                if (confirm(`Удалить «${p.label}»?`)) del.mutate(p.id);
              }}
              busy={update.isPending || del.isPending}
            />
          );
        })}
        {query.data && query.data.items.length === 0 && (
          <div style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>
            Цены ещё не заведены. Добавьте первую позицию.
          </div>
        )}
      </div>
    </div>
  );
}

function PriceRowItem({
  price,
  onSave,
  onDelete,
  busy,
}: {
  price: PriceRow;
  onSave: (patch: Record<string, unknown>) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [draft, setDraft] = useState(price);
  const changed = JSON.stringify(draft) !== JSON.stringify(price);

  return (
    <div className={styles.row}>
      <input
        className={`input ${styles.label}`}
        value={draft.label}
        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
      />
      <input
        className={`input ${styles.amount}`}
        type="number"
        value={draft.amount}
        onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) })}
      />
      <select
        className={`select ${styles.unit}`}
        value={draft.unit}
        onChange={(e) => setDraft({ ...draft, unit: e.target.value as typeof draft.unit })}
      >
        {PRICE_UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      <label className={styles.active}>
        <input
          type="checkbox"
          checked={draft.active}
          onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
        />
        на сайте
      </label>
      <div className={styles.actions}>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!changed || busy}
          onClick={() => onSave({
            label: draft.label,
            amount: draft.amount,
            unit: draft.unit,
            active: draft.active,
          })}
        >
          ✓
        </button>
        <button type="button" className="btn btn-danger" disabled={busy} onClick={onDelete}>
          ×
        </button>
      </div>
    </div>
  );
}

function NewPriceForm({ onSubmit }: { onSubmit: (v: Record<string, unknown>) => void }) {
  const [category, setCategory] = useState(PRICE_CATEGORIES[0]);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState(PRICE_UNITS[0]);

  return (
    <form
      className={`card ${styles.form}`}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          category,
          label,
          amount: Number(amount),
          unit,
          period: PRICE_PERIODS[0],
          sortOrder: 0,
          active: true,
        });
        setLabel('');
        setAmount('');
      }}
    >
      <select
        className={`select ${styles.formCategory}`}
        value={category}
        onChange={(e) => setCategory(e.target.value as typeof category)}
      >
        {PRICE_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <input
        className={`input ${styles.formLabel}`}
        placeholder="Название (например, «Бунгало, будни»)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        required
      />
      <input
        className="input"
        type="number"
        placeholder="Цена, ₽"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        required
      />
      <select className="select" value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)}>
        {PRICE_UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      <button type="submit" className={`btn btn-primary ${styles.formSubmit}`}>
        Добавить
      </button>
    </form>
  );
}
